import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { User, Lock, Eye, EyeOff } from "lucide-react";
import api from "../services/api";
import { setCurrentUser } from "../utils/auth";
import type { UserRole } from "../types/shared.types";
import "./Login.css";

interface LoginFormData {
  username: string;
  password: string;
}

interface LoginResponse {
  success: boolean;
  user?: {
    id: number;
    username: string;
    email: string;
    role: UserRole;
  };
  error?: string;
  message?: string;
}

interface ApiError {
  response?: {
    data?: {
      error?: string;
      message?: string;
    };
  };
}

interface CompanyBranding {
  companyName: string;
  logoUrl: string | null;
  primaryColor: string | null;
  accentColor: string | null;
}

const Login: React.FC = () => {
  const { t } = useTranslation("auth");
  const [formData, setFormData] = useState<LoginFormData>({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [branding, setBranding] = useState<CompanyBranding | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Fetch company branding when ?company=slug is present
  useEffect(() => {
    const companySlug = searchParams.get("company");
    if (!companySlug) return;

    const backendPort = process.env.REACT_APP_BACKEND_PORT || "5000";
    const brandingBaseUrl =
      process.env.NODE_ENV === "development"
        ? `http://${window.location.hostname}:${backendPort}/api/public`
        : `https://${window.location.hostname}/api/public`;

    // Use raw fetch to avoid auth interceptors
    fetch(`${brandingBaseUrl}/branding/${encodeURIComponent(companySlug)}`)
      .then((res) => {
        if (!res.ok) return null;
        return res.json();
      })
      .then((data) => {
        if (data?.success && data.branding) {
          setBranding(data.branding);

          // Apply CSS variables for branding colors
          if (data.branding.primaryColor) {
            document.documentElement.style.setProperty(
              "--color-primary",
              data.branding.primaryColor,
            );
          }
          if (data.branding.accentColor) {
            document.documentElement.style.setProperty(
              "--color-cta",
              data.branding.accentColor,
            );
          }
        }
      })
      .catch(() => {
        // Silently fall back to default branding
      });

    // Cleanup CSS variables on unmount
    return () => {
      document.documentElement.style.removeProperty("--color-primary");
      document.documentElement.style.removeProperty("--color-cta");
    };
  }, [searchParams]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = (await api.auth.login(formData)) as LoginResponse;

      if (response.success) {
        // Store user data (auth token is in HTTP-only cookie)
        setCurrentUser(response.user);

        // Check if onboarding is completed for this user's company
        try {
          const status = await api.onboarding.getStatus();
          if (!status.completed) {
            navigate("/onboarding/welcome");
            return;
          }
        } catch {
          // If status check fails, proceed to dashboard
        }

        navigate("/dashboard");
      } else {
        setError(
          response.error || response.message || t("login.errors.loginFailed"),
        );
      }
    } catch (err) {
      const apiError = err as ApiError;
      setError(
        apiError.response?.data?.error ||
          apiError.response?.data?.message ||
          t("login.errors.loginFailed"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Determine logo and subtitle based on branding
  const logoSrc = branding?.logoUrl || "/hirefocuz-logo.png";
  const logoAlt = branding?.companyName
    ? `${branding.companyName} Logo`
    : "HireFocuZ Logo";
  const subtitle = branding?.companyName
    ? t("login.subtitleBranded", { companyName: branding.companyName })
    : t("login.subtitle");

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src={logoSrc} alt={logoAlt} className="login-logo" />
          <h1>{t("login.title")}</h1>
          <p>{subtitle}</p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label className="form-label">
              <User size={16} /> {t("login.fields.username")}
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              className="form-input"
              placeholder={t("login.fields.usernamePlaceholder")}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              <Lock size={16} /> {t("login.fields.password")}
            </label>
            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-input"
                placeholder={t("login.fields.passwordPlaceholder")}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn-primary w-100"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="spinner"></div>
                {t("login.actions.signingIn")}
              </>
            ) : (
              t("login.actions.signIn")
            )}
          </button>
        </form>

        <div className="login-footer">
          <p>
            {t("login.footer.registerPrompt")}{" "}
            <Link to="/onboarding/register" className="link-primary">
              {t("login.footer.registerLink")}
            </Link>
          </p>
          <div className="login-legal-links">
            <a
              href="https://hirefocuz.com/terms-of-use.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("login.footer.termsOfUse", "Terms of Use")}
            </a>
            <span className="login-legal-separator">|</span>
            <a
              href="https://hirefocuz.com/privacy-policy.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("login.footer.privacyPolicy", "Privacy Policy")}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
